import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Globe,
  FileText,
  Sparkles,
  Bot,
  BarChart3,
  Camera,
  MapPin,
  Mail,
  Image as ImageIcon,
  Users,
  Settings,
  LogOut,
} from "lucide-react";
import { useAuth } from "../../auth/AuthProvider";
import { useUiLanguage } from "../../i18n/UiLanguageProvider";

// メニュー定義
type NavItem = {
  to: string;
  label: string;
  icon: React.ReactNode;
  badge?: string;
  /**
   * アクティブな時に追加で当てる class(既存 `.active` に加えて)。
   * 例: "sidebar-item--instagram-active" で紫粉橙グラデーション。
   * 未指定なら通常の青単色 (`.active` のみ)。
   */
  activeClassName?: string;
};

// イニシャル(頭文字)を avatar に表示
function getInitial(name: string): string {
  return name ? name.trim().charAt(0) : "?";
}

/**
 * 左サイドバー
 * - ロゴ / メニュー / ユーザ情報
 */
export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, tenant, logout, isSystemAdmin, hasAnyRole } = useAuth();
  const { pick } = useUiLanguage();
  const isActive = (path: string) =>
    location.pathname === path || (path !== "/" && location.pathname.startsWith(path));

  const mainNav: NavItem[] = [
    { to: "/dashboard", label: pick("ダッシュボード", "Dashboard"), icon: <LayoutDashboard size={16} /> },
    { to: "/site", label: pick("サイト管理", "Sites"), icon: <Globe size={16} /> },
    { to: "/chatbot", label: pick("チャットボット", "Chatbot"), icon: <Bot size={16} /> },
    { to: "/analytics", label: pick("アクセス解析", "Analytics"), icon: <BarChart3 size={16} /> },
    { to: "/google-business", label: pick("Googleビジネス", "Google Business"), icon: <MapPin size={16} /> },
    { to: "/mail-v2", label: pick("メール対応", "Mail"), icon: <Mail size={16} /> },
    {
      to: "/instagram-v2",
      label: "Instagram Management",
      icon: <Camera size={16} />,
      activeClassName: "sidebar-item--instagram-active",
    },
    { to: "/media", label: pick("メディア", "Media"), icon: <ImageIcon size={16} /> },
  ];

  const subNav: NavItem[] = [
    { to: "/settings", label: pick("設定", "Settings"), icon: <Settings size={16} /> },
  ];

  const systemAdminOnlyNav: NavItem[] = [
    { to: "/users", label: pick("ユーザー管理", "Users"), icon: <Users size={16} /> },
  ];

  const blogNav: NavItem = {
    to: "/blog",
    label: pick("ブログ管理", "Blog"),
    icon: <FileText size={16} />,
  };

  const aiBlogNav: NavItem = {
    to: "/ai-blog",
    label: pick("AIブログ生成", "AI Blog"),
    icon: <Sparkles size={16} />,
  };

  const roleLabelMap: Record<string, string> = {
    system_admin: pick("システム管理者", "System Admin"),
    editor: pick("編集者", "Editor"),
    viewer: pick("閲覧者", "Viewer"),
  };

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const roleLabel = user?.roles?.[0] ? roleLabelMap[user.roles[0]] ?? user.roles[0] : "";

  // Stage 3C:/blog は system_admin + editor のみ表示
  // Stage 4A-2:/ai-blog も同条件(viewer はメニュー自体を出さない)
  const canViewBlog = hasAnyRole(["system_admin", "editor"]);

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <div className="sidebar-logo-icon" aria-hidden="true">
          <img src="/nexus-logo.png" alt="" />
        </div>
        <div className="sidebar-logo-text">
          <span className="sidebar-logo-name">{tenant?.company_name ?? ""}</span>
          <span className="sidebar-logo-sub">{pick("NEXUS WP統合管理ポータル", "Unified Admin Portal")}</span>
        </div>
      </div>

      <nav className="sidebar-menu">
        <div className="sidebar-section-label">{pick("メイン", "Main")}</div>
        {mainNav.map((item) => {
          const active = isActive(item.to);
          const className = [
            "sidebar-item",
            active ? "active" : "",
            active && item.activeClassName ? item.activeClassName : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <NavLink key={item.to} to={item.to} className={className}>
              <span className="sidebar-item-icon">{item.icon}</span>
              <span>{item.label}</span>
              {item.badge && <span className="sidebar-item-badge">{item.badge}</span>}
            </NavLink>
          );
        })}

        {canViewBlog && (
          <NavLink
            key={blogNav.to}
            to={blogNav.to}
            className={`sidebar-item ${isActive(blogNav.to) ? "active" : ""}`}
          >
            <span className="sidebar-item-icon">{blogNav.icon}</span>
            <span>{blogNav.label}</span>
            {blogNav.badge && <span className="sidebar-item-badge">{blogNav.badge}</span>}
          </NavLink>
        )}

        {canViewBlog && (
          <NavLink
            key={aiBlogNav.to}
            to={aiBlogNav.to}
            className={`sidebar-item ${isActive(aiBlogNav.to) ? "active" : ""}`}
          >
            <span className="sidebar-item-icon">{aiBlogNav.icon}</span>
            <span>{aiBlogNav.label}</span>
            {aiBlogNav.badge && <span className="sidebar-item-badge">{aiBlogNav.badge}</span>}
          </NavLink>
        )}

        <div className="sidebar-section-label">{pick("管理", "Admin")}</div>
        {subNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={`sidebar-item ${isActive(item.to) ? "active" : ""}`}
          >
            <span className="sidebar-item-icon">{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        ))}
        {isSystemAdmin &&
          systemAdminOnlyNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={`sidebar-item ${isActive(item.to) ? "active" : ""}`}
            >
              <span className="sidebar-item-icon">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
      </nav>

      <div className="sidebar-user">
        <div className="sidebar-avatar">{getInitial(user?.name ?? "")}</div>
        <div className="sidebar-user-info">
          <span className="sidebar-user-name">{user?.name ?? ""}</span>
          <span className="sidebar-user-role">{roleLabel}</span>
        </div>
        <div
          className="sidebar-user-action"
          onClick={handleLogout}
          title={pick("ログアウト", "Log out")}
          role="button"
        >
          <LogOut size={14} />
        </div>
      </div>
    </aside>
  );
};
